import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  useTheme,
  Fade
} from "@mui/material";
import {
  Receipt as ReceiptIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import { useAuthToken } from "../TokenContext";
import { useCredentials } from '../useCredentials';

const InvoicesClient = () => {
  const theme = useTheme();
  // Using Artemy design tokens directly via CSS variables for robust dark mode support
  const { credentials, isLoaded, refresh } = useCredentials();
  const token = useAuthToken();
  const { isLoggedIn, user } = useSelector(state => state.auth);
  
  const [getpoddetails, setGetpoddetails] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [id, setId] = useState('');

  const config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  const S3Config = useMemo(() => ({
    bucketName: credentials.bucketName,
    region: credentials.region,
    accessKeyId: credentials.accessKeyId,
    secretAccessKey: credentials.secretAccessKey
  }), [credentials]);

  useEffect(() => {
    if (user && user.subscriptiondata && user.subscriptiondata[0]) {
      const body = {
        customerId: user.subscriptiondata[0].customerId || null,
      };
    
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      };
    
      axios.post(process.env.REACT_APP_CLIENT_LISTOFINVOICES, body, { headers: headers })
        .then((res) => {
          const responseData = JSON.parse(res.data.body);
          setGetpoddetails(responseData.body);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error("Error:", err);
          setErrorMessage("Failed to fetch data. Please try again later.");
          setIsLoading(false);
        });
    } else {
      setIsLoading(false);
      setErrorMessage("No Data Available");
    }
  }, [token]);

  return (
    <Container maxWidth="lg" sx={{ mt: 8, mb: 4 }}>
      <Fade in timeout={600}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 600,
              mb: 4,
              color: 'var(--artemy-text-primary)'
            }}
          >
            Your Invoices
          </Typography>

          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 900,
              borderRadius: '24px',
              overflow: 'hidden',
              bgcolor: 'var(--artemy-bg-card)',
              border: '1px solid var(--artemy-border)',
              p: 3,
              backdropFilter: 'blur(10px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)'
            }}
          >
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress sx={{ color: 'var(--artemy-accent)' }} />
                <Typography sx={{ mt: 2, color: 'var(--artemy-text-secondary)', fontFamily: 'var(--artemy-font-mono)', fontSize: '13px' }}>
                  FETCHING INVOICES...
                </Typography>
              </Box>
            ) : errorMessage ? (
              <Typography sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
                {errorMessage}
              </Typography>
            ) : !Array.isArray(getpoddetails) || getpoddetails.length === 0 ? (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <ReceiptIcon sx={{ fontSize: '4rem', color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                  You don't have any invoices.
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your invoice history will appear here once you start using our services
                </Typography>
              </Box>
            ) : (
              <TableContainer 
                component={Box} 
                sx={{ 
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '1px solid var(--artemy-border)',
                  bgcolor: 'transparent'
                }}
              >
                <Table 
                  sx={{ 
                    maxWidth: 900, 
                    fontFamily: 'var(--artemy-font-body)',
                    bgcolor: 'transparent'
                  }} 
                  aria-label="simple table"
                >
                  <TableHead 
                    sx={{ 
                      backgroundColor: 'var(--artemy-bg-elevated)',
                      '& td, & th': { 
                        fontFamily: 'var(--artemy-font-mono)', 
                        fontSize: '11px', 
                        color: 'var(--artemy-text-secondary)', 
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        py: 2,
                        borderBottom: '1px solid var(--artemy-border)'
                      } 
                    }}
                  >
                    <TableRow>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Amount Paid
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Date
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Status
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                        Amount Due
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {getpoddetails.map((x, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          '& td, & th': {
                            fontFamily: 'var(--artemy-font-body)',
                            fontSize: '14px',
                            color: 'var(--artemy-text-primary)',
                            borderBottom: '1px solid var(--artemy-border)',
                            py: 2
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.02)',
                          },
                          '&:last-child td, &:last-child th': { 
                            border: 0, 
                          },
                        }}
                      >
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          ${x.amount_paid}
                        </TableCell>
                        <TableCell align="right" sx={{ color: 'var(--artemy-text-secondary)' }}>
                          {x.created}
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'inline-flex',
                              px: 1.5,
                              py: 0.5,
                              borderRadius: '6px',
                              fontSize: '11px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                              bgcolor: x.status === 'paid' ? 'rgba(46, 213, 115, 0.15)' : 'rgba(255, 71, 87, 0.15)',
                              color: x.status === 'paid' ? '#2ed573' : '#ff4757',
                              border: `1px solid ${x.status === 'paid' ? 'rgba(46, 213, 115, 0.2)' : 'rgba(255, 71, 87, 0.2)'}`
                            }}
                          >
                            {x.status}
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ color: x.amount_due > 0 ? 'var(--artemy-accent)' : 'var(--artemy-text-secondary)' }}>
                          ${x.amount_due}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Box>
      </Fade>
    </Container>
  );
};

export default InvoicesClient;